/**
 * Session Check Hook
 *
 * Automatically checks and maintains user session state
 * Uses Better Auth's native useSession hook
 *
 * IMPORTANT: This hook does NOT block rendering - it runs in background
 */

import { useEffect, useRef } from 'react';
import { useSession as useBetterAuthSession } from '@/shared/lib/auth';
import { useAuthStore } from '@/shared/stores/auth-store';
import { usersApi } from '@/shared/lib/api/users-api';
import { shouldRefreshSession, markSessionRefreshed } from '@/shared/lib/session-refresh';

/**
 * Hook to automatically check and maintain session state
 *
 * Features:
 * - Uses Better Auth native session management
 * - Checks session on mount (non-blocking)
 * - Refetches on window focus
 * - Updates auth store on session change
 * - Graceful error handling (continues app on timeout/error)
 * - Does NOT block page rendering
 *
 * Usage:
 *
 * function App() {
 *   useSessionCheck(); // Non-blocking - runs in background
 *   return <RouterProvider />;
 * }
 */
export function useSessionCheck() {
  const setUser = useAuthStore((state) => state.setUser);
  const clearUser = useAuthStore((state) => state.clearUser);
  const setSessionInitialized = useAuthStore((state) => state.setSessionInitialized);
  const fetchingUserRef = useRef(false);

  // Use Better Auth native session hook
  const { data: session, isPending, error } = useBetterAuthSession();

  // Sync session with auth store
  useEffect(() => {
    console.log('[useSessionCheck] Effect triggered - session:', session, 'isPending:', isPending);

    if (session?.user) {
      console.log('[useSessionCheck] Session exists, fetching full user data from API');

      // Prevent duplicate fetches
      if (fetchingUserRef.current) {
        return;
      }

      // Fetch full user data from API to get hasCompletedOnboarding
      // This also refreshes the session via cookie (withCredentials: true)
      const fetchUserData = async () => {
        fetchingUserRef.current = true;

        // Check if session needs refresh (at least once per 24h)
        const needsRefresh = shouldRefreshSession();
        if (needsRefresh) {
          console.log('[useSessionCheck] Session refresh needed (>24h since last refresh)');
        }

        try {
          const userResponse = await usersApi.me();
          console.log('[useSessionCheck] User data from /users/me:', userResponse);

          setUser({
            id: userResponse.id,
            email: userResponse.email,
            name: userResponse.name,
            role: userResponse.role,
            emailVerified: userResponse.emailVerified,
            image: userResponse.image,
            createdAt: userResponse.createdAt,
            updatedAt: userResponse.updatedAt,
            onboardingCompleted: userResponse.hasCompletedOnboarding,
          });

          // Mark session as refreshed (API call renews the cookie)
          if (needsRefresh) {
            markSessionRefreshed();
            console.log('[useSessionCheck] Session refresh marked');
          }
        } catch (err) {
          console.error('[useSessionCheck] Failed to fetch user data, using session fallback:', err);
          // Fallback to session data
          const backendUser = session.user as {
            role?: 'DOG_OWNER' | 'VETERINARIAN' | 'ADMIN';
            hasCompletedOnboarding?: boolean;
          };

          setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.name,
            role: backendUser.role || 'DOG_OWNER',
            emailVerified: session.user.emailVerified || false,
            image: session.user.image || null,
            createdAt: session.user.createdAt.toISOString(),
            updatedAt: session.user.updatedAt.toISOString(),
            onboardingCompleted: backendUser.hasCompletedOnboarding ?? false,
          });
        } finally {
          fetchingUserRef.current = false;
          setSessionInitialized(true);
        }
      };

      void fetchUserData();
    } else if (!isPending) {
      // Only clear user if there's no existing user in store
      // This prevents clearing user immediately after login when Better Auth hasn't synced yet
      const currentUser = useAuthStore.getState().user;

      if (!currentUser) {
        console.log('[useSessionCheck] No session, no pending, no current user - clearing and initializing');
        clearUser();
        setSessionInitialized(true);
      } else {
        console.log('[useSessionCheck] No session but user exists in store - keeping user (Better Auth sync pending)');
        // Don't clear - user was just set by login, Better Auth will sync eventually
        if (!useAuthStore.getState()._sessionInitialized) {
          setSessionInitialized(true);
        }
      }
    }
  }, [session, isPending, setUser, clearUser, setSessionInitialized]);

  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error('[useSessionCheck] Session check failed:', error);
      // Clear user on error to prevent stuck loading state
      clearUser();
      setSessionInitialized(true);
    }
  }, [error, clearUser, setSessionInitialized]);

  return {
    session,
    // Retorna true enquanto Better Auth está carregando OU enquanto está buscando dados do usuário
    isLoading: isPending || (!!session?.user && !useAuthStore.getState()._sessionInitialized),
    error,
    isAuthenticated: !!session?.user,
  };
}
